/**
 * Journal spine helpers — Increment B/C durability for case_events.
 *
 * Design:
 *  - Event-sourced runs (append-only) stay the source of truth on the server.
 *  - Stale-while-revalidate client cache (24h TTL) makes Journal feel instant.
 *  - Optimistic merge of session runLog so POST races never blank the list.
 *  - Write-ahead outbox retries failed appends (longer effective TTL for writes).
 *  - Demo seed (INGEST_STEPS → synthetic card.run events) so Replay works on day one.
 */
import { INGEST_STEPS, SEED_NODES } from '../../data/fusionSeed';
import type { RunEvent } from '../../stores/useIntakeStore';

const API = ((import.meta.env as unknown as Record<string, string>).VITE_API_URL) || 'http://localhost:8000/api/v1';

/** Client cache TTL — Journal stays warm across tab switches / soft reloads. */
export const JOURNAL_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

const CACHE_PREFIX = 'fusion-journal-cache:v1:';
const OUTBOX_KEY = 'fusion-journal-outbox:v1';
const SEEDED_PREFIX = 'fusion-journal-seeded:v1:';

export interface JournalEntry {
  id: string;
  event_type?: string;
  ts: number;
  title: string;
  summary: string;
  tier?: string | null;
  targetNodeId?: string;
  nodeLabel?: string;
  synthetic?: boolean;
  pending?: boolean;
}

type CacheBlob = { savedAt: number; entries: JournalEntry[] };
type OutboxItem = {
  caseId: string;
  event_type: string;
  payload: Record<string, unknown>;
  clientId: string;
  attempts: number;
};

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('token') || '';
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

function firstSeedNodeForStep(step: number) {
  return SEED_NODES.find((n) => (n.step ?? 1) === step);
}

/** Synthetic Journal frames aligned with the demo ingestion timeline. */
export function seedJournalEntries(baseTs = Date.now() - 6 * 60 * 1000): JournalEntry[] {
  return INGEST_STEPS.map((s, i) => {
    const node = firstSeedNodeForStep(s.n);
    return {
      id: `seed-ingest-${s.n}`,
      ts: baseTs + i * 75_000,
      title: s.title,
      summary: s.sub,
      tier: s.n <= 3 ? 'deterministic' : 'scored',
      targetNodeId: node?.id,
      nodeLabel: node?.label,
      synthetic: true,
    };
  });
}

export function formatFindingEventSummary(eventType: string, payload: Record<string, unknown>): string | null {
  const rowKey = String(payload.row_key || 'target');
  if (eventType === 'finding_assigned') {
    const assignee = String(payload.assignee_id ?? payload.assigned_to ?? payload.assignee ?? 'unassigned');
    return `Finding ${rowKey} assigned to ${assignee}`;
  }
  if (eventType === 'finding_status') {
    const status = String(payload.status || 'open');
    const reason = payload.reason || payload.status_reason;
    return reason
      ? `Finding ${rowKey} status -> ${status} (${String(reason)})`
      : `Finding ${rowKey} status -> ${status}`;
  }
  if (eventType === 'finding_comment') {
    const text = String(payload.text || payload.comment || '');
    const truncated = text.length > 60 ? `${text.slice(0, 57)}...` : text;
    return `Finding ${rowKey} comment: "${truncated}"`;
  }
  return null;
}

export function mapServerEvent(e: {
  id: string;
  event_type: string;
  payload?: Record<string, unknown>;
  ts?: string | null;
}): JournalEntry {
  const p = e.payload || {};
  if (e.event_type === 'screening_suppressed' || e.event_type === 'screening_unsuppressed') {
    const isSupp = e.event_type === 'screening_suppressed';
    const ident = String(p.identifier || p.canonical_id || '');
    const label = String(p.label || p.hit_key || 'hit');
    const officer = String(p.officer || 'officer');
    const reason = String(p.reason || '');
    const defaultTitle = isSupp
      ? `Screening hit suppressed (${ident || label})`
      : `Screening hit unsuppressed (${ident || label})`;
    const defaultSummary = isSupp
      ? `${label && label !== ident ? `"${label}" ` : ''}suppressed by ${officer}: ${reason}`
      : `Restored by ${officer}: ${reason}`;
    return {
      id: e.id,
      event_type: e.event_type,
      ts: e.ts ? Date.parse(e.ts) : Date.now(),
      title: String(p.title || defaultTitle),
      summary: String(p.summary || defaultSummary),
      tier: (p.tier as string) ?? 'deterministic',
      targetNodeId: (p.targetNodeId || p.canonical_id || p.identifier) as string | undefined,
      nodeLabel: (p.nodeLabel || p.label || p.identifier) as string | undefined,
      synthetic: p.synthetic === true,
    };
  }

  const findingLine = formatFindingEventSummary(e.event_type, p);
  let title = findingLine || String(p.title || e.event_type);
  let summary = findingLine || String(p.summary || '');
  let tier: string | null = (p.tier as string) ?? (findingLine ? 'review' : null);

  if (e.event_type === 'case_watch_toggled') {
    tier = 'deterministic';
    const onOff = p.enabled ? 'on' : 'off';
    const who = p.set_by || 'officer';
    title = `Case watch toggled ${onOff}`;
    summary = `Watch turned ${onOff} by ${who}`;
  } else if (e.event_type === 'case_watch_change') {
    tier = 'deterministic';
    const wShort = String(p.wallet_short || (String(p.wallet || '').slice(0, 8)));
    const kind = String(p.kind || 'change');
    title = `${wShort} · ${kind}`;
    if (kind === 'inbound_transfer') {
      const count = (p.after as Record<string, unknown>)?.count ?? '';
      const totals = (p.after as Record<string, unknown>)?.totals as Record<string, number> | undefined;
      const totStr = totals ? Object.entries(totals).map(([tok, amt]) => `${amt} ${tok}`).join(', ') : '';
      summary = `${wShort} ${kind}: ${count} transfer(s) (${totStr || 'new inbound'})`;
    } else {
      const b = typeof p.before === 'object' ? JSON.stringify(p.before) : String(p.before ?? 'none');
      const a = typeof p.after === 'object' ? JSON.stringify(p.after) : String(p.after ?? 'none');
      summary = `${wShort} ${kind}: ${b} -> ${a}`;
    }
  } else if (e.event_type === 'case_watch_digest') {
    tier = 'deterministic';
    title = `Watch daily digest (${p.day || ''})`;
    const count = p.total_changes ?? 0;
    const wallets = Array.isArray(p.wallets_changed) ? p.wallets_changed.length : 0;
    const notTested = p.not_tested_count ? ` (${p.not_tested_count} not tested)` : '';
    summary = `${count} change(s) across ${wallets} wallet(s)${notTested}`;
  }
  return {
    id: e.id,
    event_type: e.event_type,
    ts: e.ts ? Date.parse(e.ts) : Date.now(),
    title,
    summary,
    tier,
    targetNodeId: p.targetNodeId as string | undefined,
    nodeLabel: p.nodeLabel as string | undefined,
    synthetic: p.synthetic === true,
  };
}

export function runEventToJournal(e: RunEvent): JournalEntry {
  return {
    id: e.id,
    ts: e.ts,
    title: e.title,
    summary: e.summary,
    tier: e.tier,
    targetNodeId: e.targetNodeId,
    nodeLabel: e.nodeLabel,
    pending: true,
  };
}

function fingerprint(e: Pick<JournalEntry, 'title' | 'summary' | 'targetNodeId'>): string {
  return `${e.title}|${e.summary}|${e.targetNodeId || ''}`;
}

/**
 * Newest-first list: server wins on id; optimistic rows fill gaps until POST lands.
 * Drops optimistic duplicates that already appear on the server (same fingerprint, ±2m).
 */
export function mergeJournalEntries(server: JournalEntry[], optimistic: JournalEntry[]): JournalEntry[] {
  const byId = new Map<string, JournalEntry>();
  for (const e of server) byId.set(e.id, e);

  for (const e of optimistic) {
    if (byId.has(e.id)) continue;
    const nearDup = server.some(
      (s) => fingerprint(s) === fingerprint(e) && Math.abs(s.ts - e.ts) < 120_000,
    );
    if (nearDup) continue;
    byId.set(e.id, e);
  }

  return [...byId.values()].sort((a, b) => b.ts - a.ts);
}

export function readEventsCache(caseId: string): JournalEntry[] | null {
  try {
    const raw = sessionStorage.getItem(CACHE_PREFIX + caseId);
    if (!raw) return null;
    const blob = JSON.parse(raw) as CacheBlob;
    if (!blob?.savedAt || Date.now() - blob.savedAt > JOURNAL_CACHE_TTL_MS) return null;
    return Array.isArray(blob.entries) ? blob.entries : null;
  } catch {
    return null;
  }
}

export function writeEventsCache(caseId: string, entries: JournalEntry[]): void {
  try {
    const blob: CacheBlob = { savedAt: Date.now(), entries };
    sessionStorage.setItem(CACHE_PREFIX + caseId, JSON.stringify(blob));
  } catch { /* quota / private mode */ }
}

function readOutbox(): OutboxItem[] {
  try {
    const raw = localStorage.getItem(OUTBOX_KEY);
    return raw ? (JSON.parse(raw) as OutboxItem[]) : [];
  } catch {
    return [];
  }
}

function writeOutbox(items: OutboxItem[]): void {
  try {
    localStorage.setItem(OUTBOX_KEY, JSON.stringify(items.slice(0, 100)));
  } catch { /* ignore */ }
}

export function enqueueOutbox(item: Omit<OutboxItem, 'attempts'>): void {
  const next = readOutbox().filter((x) => x.clientId !== item.clientId);
  next.push({ ...item, attempts: 0 });
  writeOutbox(next);
}

/** Append one event; on failure park in outbox for flushOutbox. */
export async function persistCaseEvent(
  caseId: string,
  event_type: string,
  payload: Record<string, unknown>,
  clientId: string,
): Promise<string | null> {
  try {
    const r = await fetch(`${API}/canvas/${caseId}/events`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ event_type, payload }),
    });
    if (!r.ok) throw new Error(`append ${r.status}`);
    const d = (await r.json()) as { id?: string };
    // Drop matching outbox row if any
    writeOutbox(readOutbox().filter((x) => x.clientId !== clientId));
    return d.id || null;
  } catch {
    enqueueOutbox({ caseId, event_type, payload, clientId });
    return null;
  }
}

/** Drain write-ahead outbox (call on bind / Journal mount). Max 3 attempts per item. */
export async function flushOutbox(caseId?: string): Promise<number> {
  const items = readOutbox().filter((x) => !caseId || x.caseId === caseId);
  if (!items.length) return 0;
  let flushed = 0;
  const remain: OutboxItem[] = [];
  for (const item of readOutbox()) {
    if (caseId && item.caseId !== caseId) {
      remain.push(item);
      continue;
    }
    if (item.attempts >= 3) continue; // drop poison
    try {
      const r = await fetch(`${API}/canvas/${item.caseId}/events`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ event_type: item.event_type, payload: item.payload }),
      });
      if (!r.ok) throw new Error(String(r.status));
      flushed += 1;
    } catch {
      remain.push({ ...item, attempts: item.attempts + 1 });
    }
  }
  writeOutbox(remain);
  return flushed;
}

export async function fetchCaseEvents(caseId: string): Promise<JournalEntry[]> {
  const r = await fetch(`${API}/canvas/${caseId}/events?limit=200`, { headers: authHeaders() });
  if (!r.ok) return [];
  const d = (await r.json()) as { events?: Parameters<typeof mapServerEvent>[0][] };
  return (d.events || []).map(mapServerEvent);
}

/**
 * If the sandbox Journal is empty, append synthetic ingest events once so
 * Increment C Replay has something to play on first open. Real cases never call this.
 */
export async function ensureDemoSeedEvents(caseId: string): Promise<boolean> {
  if (typeof localStorage !== 'undefined' && localStorage.getItem(SEEDED_PREFIX + caseId) === '1') {
    return false;
  }
  const existing = await fetchCaseEvents(caseId);
  if (existing.length > 0) {
    try { localStorage.setItem(SEEDED_PREFIX + caseId, '1'); } catch { /* ignore */ }
    writeEventsCache(caseId, existing);
    return false;
  }
  const seeds = seedJournalEntries();
  for (const s of seeds) {
    await persistCaseEvent(
      caseId,
      `card.run:seed.ingest:${s.id}`,
      {
        title: s.title,
        summary: s.summary,
        tier: s.tier,
        registryId: 'seed.ingest',
        targetNodeId: s.targetNodeId,
        nodeLabel: s.nodeLabel,
        synthetic: true,
      },
      s.id,
    );
  }
  try { localStorage.setItem(SEEDED_PREFIX + caseId, '1'); } catch { /* ignore */ }
  const refreshed = await fetchCaseEvents(caseId);
  writeEventsCache(caseId, refreshed.length ? refreshed : seeds);
  return true;
}
